import React, { useState, useEffect } from "react";
import axios from "axios";
import { Spinner, Tabs } from "@opendate-app/setlist";
import { toastError } from "../../../../shared/toastHelper";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import SelectableTemplateCard from "./SelectableTemplateCard";

const TABS = ["Opendate Blueprints", "Team Blueprints", "Existing Emails"];

const titleStyle = {
  fontSize: 18,
  lineHeight: "28px",
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const subtitleStyle = {
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--sl-text-tertiary)",
};

const gridStyle = {
  display: "flex",
  gap: 16,
  flexWrap: "wrap",
};

const loadingStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "200px",
  width: "100%",
};

const scratchLabelStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const emptyStyle = {
  margin: 0,
  fontSize: 14,
  color: "var(--sl-text-tertiary)",
};

const contentSelectStyle = {
  display: "flex",
  flexDirection: "column",
  width: 208,
  height: "fit-content",
  borderRadius: 12,
  padding: 16,
  gap: 16,
  backgroundColor: "var(--sl-bg-primary)",
  boxShadow: "var(--sl-shadow-xs)",
  border: "1px solid var(--sl-border-secondary)",
  textAlign: "left",
  cursor: "pointer",
};

export default ({
  updateBeefreeTemplate,
  tab,
  setTab,
}) => {
  const {
    csrfToken,
    team,
    selectedNode,
  } = useCustomCampaignShowContext();

  const [previousBroadcastTemplates, setPreviousBroadcastTemplates] = useState([]);
  const [adminBlueprints, setAdminBlueprints] = useState([]);
  const [teamBlueprints, setTeamBlueprints] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [blueprintsLoading, setBlueprintsLoading] = useState(false);
  const [teamBlueprintsLoading, setTeamBlueprintsLoading] = useState(false);

  useEffect(() => {
    if (tab === "Existing Emails" && previousBroadcastTemplates.length === 0) {
      setTemplatesLoading(true);
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/beefree_templates.json`)
        .then(({ data }) => {
          setPreviousBroadcastTemplates(data);
          setTemplatesLoading(false);
        })
        .catch(() => {
          toastError("Could not load templates, please try again");
          setTemplatesLoading(false);
        });
    }

    if (tab === "Opendate Blueprints" && adminBlueprints.length === 0) {
      setBlueprintsLoading(true);
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/beefree_templates/admin_blueprints.json`)
        .then(({ data }) => {
          setAdminBlueprints(data);
          setBlueprintsLoading(false);
        })
        .catch(() => {
          toastError("Could not load admin blueprints, please try again");
          setBlueprintsLoading(false);
        });
    }

    if (tab === "Team Blueprints" && teamBlueprints.length === 0) {
      setTeamBlueprintsLoading(true);
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/beefree_templates/team_blueprints.json`)
        .then(({ data }) => {
          setTeamBlueprints(data);
          setTeamBlueprintsLoading(false);
        })
        .catch(() => {
          toastError("Could not load team blueprints, please try again");
          setTeamBlueprintsLoading(false);
        });
    }
  }, [tab]);

  const buildFromTemplate = (template, copiedFromBlueprintId = null) => {
    updateBeefreeTemplate({
      team_id: team.id,
      templatable_id: selectedNode.internal_id,
      templatable_type: "WorkflowNode",
      template: template,
      copied_from_blueprint_id: copiedFromBlueprintId,
    });
  }

  return (
    <>
      <div>
        <div style={titleStyle}>
          Content
        </div>
        <div style={subtitleStyle}>
          Design the content of your email.
        </div>
      </div>
      <Tabs
        aria-label="Template sources"
        value={tab}
        onChange={setTab}
        items={TABS.map((value) => ({ value, label: value }))}
      />
      {
        tab === "Opendate Blueprints" ? (
          <div style={gridStyle}>
            <button
              type="button"
              style={contentSelectStyle}
              onClick={(e) => { e.preventDefault(); buildFromTemplate(`{"page": {"body": {"rows": []}}}`); }}
            >
              <img src="/scratch.jpg" alt="" />
              <div style={scratchLabelStyle}>
                Start from Scratch
              </div>
            </button>
            {blueprintsLoading ? (
              <div style={loadingStyle}>
                <Spinner size="md" aria-label="Loading templates" />
              </div>
            ) : adminBlueprints.length > 0 ? (
              adminBlueprints.map((blueprint, index) => (
                <SelectableTemplateCard
                  onSelect={() => buildFromTemplate(blueprint.template, null)}
                  template={{
                    ...blueprint,
                    templatable: { name: blueprint.name }
                  }}
                  key={index}
                />
              ))
            ) : null}
          </div>
        ) : tab === "Team Blueprints" ? (
          <div style={gridStyle}>
            {teamBlueprintsLoading ? (
              <div style={loadingStyle}>
                <Spinner size="md" aria-label="Loading templates" />
              </div>
            ) : teamBlueprints.length > 0 ? (
              teamBlueprints.map((blueprint, index) => (
                <SelectableTemplateCard
                  onSelect={() => buildFromTemplate(blueprint.template, blueprint.id)}
                  template={{
                    ...blueprint,
                    templatable: { name: blueprint.name }
                  }}
                  key={index}
                />
              ))
            ) : (
              <p style={emptyStyle}>No team blueprints yet. Save a template as a blueprint to get started.</p>
            )}
          </div>
        ) : (
          <div style={gridStyle}>
            {templatesLoading ? (
              <div style={loadingStyle}>
                <Spinner size="md" aria-label="Loading templates" />
              </div>
            ) : previousBroadcastTemplates.length > 0 ? (
              previousBroadcastTemplates.map((template, index) => (
                <SelectableTemplateCard
                  onSelect={() => buildFromTemplate(template.template)}
                  template={template}
                  key={index}
                />
              ))
            ) : null}
          </div>
        )
      }
    </>
  );
};
